import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Label } from "../../ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { toast } from "sonner";
import { Save, FileText, Calendar, AlertTriangle, Check, X, History, ScrollText, MessageSquare, MessageSquare as MessageSquareIcon, User, Mail, Phone as PhoneIcon, Smartphone, ChevronRight, Euro } from "lucide-react";
import WhatsAppTemplateButton from "../WhatsAppTemplateButton";
import NAWAddressBlock from "./NAWAddressBlock";
import AdditionalContactsBlock from "./AdditionalContactsBlock";
import CustomerTypeBadge, { CUSTOMER_TYPE_LABELS } from "../CustomerTypeBadge";
import KlantTarievenTab from "./KlantTarievenTab";
import KlantOpenstaandTab from "./KlantOpenstaandTab";
import CustomerRecipientsPanel from "./CustomerRecipientsPanel";

const STATUSES = ["nieuw", "actief", "wacht_info", "wacht_betaling", "afgerond", "geblokkeerd"];
const STATUS_LABELS = {
  nieuw: "Nieuw", actief: "Actief", wacht_info: "Wacht op informatie",
  wacht_betaling: "Wacht op betaling", afgerond: "Afgerond", geblokkeerd: "Geblokkeerd",
};
// Fase 2 — drie primaire types vooraan (zakelijk/particulier/onbekend),
// legacy types (webshop/shop_drop_ship/terugkerend) blijven beschikbaar voor
// backwards-compat met al ingevoerde klantdata.
const PRIMARY_TYPES = ["zakelijk", "particulier", "onbekend"];
const LEGACY_TYPES = ["webshop", "shop_drop_ship", "terugkerend"];
const TYPE_LABELS = CUSTOMER_TYPE_LABELS;
const QUOTE_STATUS_LABELS = {
  nieuw: "Nieuw", new: "Nieuw", in_behandeling: "In behandeling",
  wacht_klantinfo: "Wacht klantinfo", wacht_documenten: "Wacht documenten",
  wacht_betaling: "Wacht betaling", goedgekeurd: "Goedgekeurd",
  afgewezen: "Afgewezen", verlopen: "Verlopen", omgezet: "Omgezet",
};

const STATUS_COLORS = {
  nieuw: "bg-blue-100 text-blue-700",
  actief: "bg-green-100 text-green-700",
  wacht_info: "bg-yellow-100 text-yellow-700",
  wacht_betaling: "bg-red-100 text-red-700",
  afgerond: "bg-gray-100 text-gray-700",
  geblokkeerd: "bg-gray-300 text-gray-800",
};

const FIELD_LABELS = {
  first_name: "Voornaam", last_name: "Achternaam", company_name: "Bedrijfsnaam",
  email: "E-mailadres", phone: "Telefoon", whatsapp: "WhatsApp",
  address: "Adres", postal_code: "Postcode", city: "Stad", country: "Land",
};

// Contactvelden die als losse Input renderen (adresvelden komen via
// NAWAddressBlock met PDOK-lookup, los daaronder).
const CONTACT_FIELDS = ["first_name", "last_name", "company_name", "email", "phone", "whatsapp"];

function fmtTs(ts) { return ts?.slice(0, 16).replace("T", " "); }

// P1 — Overzicht-kaart helpers. Stabiel buiten KlantDetail gedefinieerd zodat
// React niet bij elke re-render een nieuwe componenttype ziet.
function SectionCard({ title, children, testid }) {
  return (
    <div className="bg-white border border-gray-200 p-4" data-testid={testid}>
      <h3 className="font-chivo font-black text-xs uppercase tracking-wider text-[#0055FF] mb-3 border-b border-gray-100 pb-2">
        {title}
      </h3>
      <div className="space-y-1.5 text-sm">{children}</div>
    </div>
  );
}

function OverzichtRow({ label, value, mono = false, testid }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
      <span className="text-[10px] uppercase tracking-wide text-gray-500 font-bold min-w-[110px]">{label}</span>
      <span className={`text-gray-800 break-words ${mono ? "font-mono text-[11px]" : ""}`} data-testid={testid}>
        {value || value === 0 ? value : <span className="text-gray-300">—</span>}
      </span>
    </div>
  );
}

export default function KlantDetail({ customerId, onChanged, onNavigateSection }) {
  const { user, hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("overzicht");
  const [showTechnical, setShowTechnical] = useState(false);
  // 360° Customer Workspace · Feb 2026 — KPI-strip + unified tabs
  const [wsSummary, setWsSummary] = useState(null);
  const [wsCommunications, setWsCommunications] = useState(null);
  const [wsInvoices, setWsInvoices] = useState(null);
  const [wsBookings, setWsBookings] = useState(null);
  const [wsShipments, setWsShipments] = useState(null);
  const [wsDocuments, setWsDocuments] = useState(null);

  const canEditFull = hasPermission("customers:edit_full");
  const canEditContact = hasPermission("customers:edit_contact");
  // Technische velden (phone_norm, address_key) zichtbaar voor Owner/Admin
  // — niet voor gewone medewerkers per spec.
  const canViewTechnical = ["owner", "admin"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4, rws] = await Promise.all([
        adminApi.get(`/admin/customers/${customerId}`),
        adminApi.get(`/admin/customers/${customerId}/proposals`).catch(() => ({ data: [] })),
        adminApi.get(`/admin/customers/${customerId}/timeline`).catch(() => ({ data: [] })),
        adminApi.get(`/admin/customers/${customerId}/audit-logs`).catch(() => ({ data: [] })),
        // 360° Customer Workspace summary — 1 geconsolideerde call
        adminApi.get(`/admin/customers/${customerId}/workspace-summary`).catch(() => ({ data: null })),
      ]);
      setData(r1.data);
      setForm(r1.data.customer || {});
      setProposals(r2.data || []);
      setTimeline(r3.data || []);
      setAuditLogs(r4.data || []);
      setWsSummary(rws.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  // Lazy-load per tab (na eerste keer aanwezig). Iedere tab houdt zijn eigen
  // data-cache zodat wisselen niet steeds refetch triggert.
  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        if (tab === "communicatie" && wsCommunications === null) {
          const r = await adminApi.get(`/admin/customers/${customerId}/communications?limit=100`);
          setWsCommunications(r.data?.items || []);
        } else if (tab === "facturen" && wsInvoices === null) {
          const r = await adminApi.get(`/admin/sales-invoices`, {
            params: { customer_id: customerId, limit: 50 },
          });
          setWsInvoices(Array.isArray(r.data) ? r.data : (r.data?.items || []));
        } else if (tab === "boekingen" && wsBookings === null) {
          const r = await adminApi.get(`/admin/booking-forms`, {
            params: { customer_id: customerId, limit: 50 },
          }).catch(() => ({ data: [] }));
          const raw = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          // 🛡 Client-side safety guard — voorkomt data-leak mocht ooit een
          // backend-filter regresseren. Filtert nogmaals op customer_id.
          setWsBookings(raw.filter((b) => !b.customer_id || b.customer_id === customerId));
        } else if (tab === "zendingen" && wsShipments === null) {
          const r = await adminApi.get(`/admin/shipping-labels`, {
            params: { customer_id: customerId, limit: 50 },
          }).catch(() => ({ data: [] }));
          const raw = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          // 🛡 Zelfde safety guard. Legacy records zonder customer_id
          // matchen we op recipient.email tegen de klant-email uit
          // wsSummary indien beschikbaar.
          const cEmail = (wsSummary?.customer?.email || "").toLowerCase();
          setWsShipments(raw.filter((s) => {
            if (s.customer_id && s.customer_id === customerId) return true;
            if (cEmail && s.recipient?.email?.toLowerCase() === cEmail) return true;
            // Als het item geen customer_id EN geen matching recipient heeft,
            // filter WEL uit (voorkeur: onder-tonen boven data-leak).
            if (!s.customer_id && !cEmail) return false;
            return false;
          }));
        } else if (tab === "documenten" && wsDocuments === null) {
          // Documenten: aggregeer paklijsten + boekingsformulieren + facturen
          setWsDocuments([]); // stub — invullen bij Documenten-endpoint uitwerking
        }
      } catch {/* silent */ }
    })();
  }, [tab, customerId, wsCommunications, wsInvoices, wsBookings, wsShipments, wsDocuments]);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k, v]) =>
          ["first_name", "last_name", "company_name", "email", "phone", "whatsapp",
           "address", "postal_code", "city", "country",
           "house_number", "house_number_addition",
           "customer_type", "notes",
           "btw_number", "kvk_number",
           // Batch 3 · Fase 2 — Agent classification toggle
           "is_agent",
           // Batch 3 · Fase 3 — Extra contactpersonen
           "additional_contacts"].includes(k)
          && v !== undefined && v !== null
        )
      );
      await adminApi.put(`/admin/customers/${customerId}`, payload);
      toast.success("Klant bijgewerkt");
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const onChangeStatus = async (status) => {
    try {
      await adminApi.put(`/admin/customers/${customerId}/status`, { status });
      toast.success("Status bijgewerkt");
      load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onResolveProposal = async (proposalId, action) => {
    try {
      await adminApi.post(`/admin/customers/${customerId}/proposals/${proposalId}/${action}`);
      toast.success(action === "accept" ? "Wijziging overgenomen" : "Wijziging afgewezen");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) return <div className="text-gray-500">Klant laden...</div>;
  const c = data.customer;
  const quotes = data.quotes || [];
  const pickups = data.pickups || [];
  const activity = data.activity || {
    quote_count: quotes.length, pickup_count: pickups.length,
    invoice_count: 0, label_count: 0,
  };
  const proposalCount = proposals.length;

  // P1 — section card helper voor het overzichtsblok. Houd consistent in
  // /app/design_guidelines.md.
  const Row = OverzichtRow;

  return (
    <div data-testid={`klant-detail-${customerId}`} className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-gray-200 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-chivo font-black text-2xl text-[#0A192F]">
              {[c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "Onbekend"}
            </h1>
            {c.company_name && <div className="text-sm text-gray-600 mt-1">{c.company_name}</div>}
            <div className="mt-2 text-xs text-gray-500">
              Klant-ID: <span className="font-mono">{c.id}</span> · Bron: {c.source || "—"} · Aangemaakt {fmtTs(c.created_at)}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <WhatsAppTemplateButton customerId={c.id} testIdPrefix="klant-wa" />
            <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${STATUS_COLORS[c.status] || "bg-gray-100"}`}>
              {STATUS_LABELS[c.status] || c.status}
            </span>
            <Select value={c.status} onValueChange={onChangeStatus} disabled={!canEditContact}>
              <SelectTrigger className="rounded-none w-44" data-testid="klant-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {proposalCount > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-300 p-4 flex items-start gap-3" data-testid="klant-proposals-banner">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-chivo font-bold text-[#0A192F]">
              {proposalCount} wijzigingsvoorstel{proposalCount > 1 ? "len" : ""} uit offerteformulier
            </div>
            <div className="text-xs text-gray-600 mt-0.5">
              Nieuwe gegevens uit een offerte wijken af van de bestaande klantgegevens. Bekijk en kies per voorstel.
            </div>
            <Button onClick={() => setTab("contactgegevens")} size="sm" className="mt-2 rounded-none bg-[#0A192F] hover:bg-[#0055FF] text-white text-xs uppercase font-chivo font-bold" data-testid="klant-view-proposals">
              Bekijk voorstellen
            </Button>
          </div>
        </div>
      )}

      {/* 360° Customer Workspace — KPI-strip · Feb 2026 */}
      {wsSummary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="klant-kpi-strip">
          <KpiPill label="Omzet totaal" value={fmtEur(wsSummary.financials?.revenue_total)} tone="emerald" testid="klant-kpi-revenue" />
          <KpiPill label="Openstaand" value={fmtEur(wsSummary.financials?.outstanding_total)} tone={wsSummary.financials?.outstanding_total > 0 ? "amber" : "slate"} testid="klant-kpi-outstanding" />
          <KpiPill label="Vervallen" value={fmtEur(wsSummary.financials?.overdue_total)} tone={wsSummary.financials?.overdue_total > 0 ? "red" : "slate"} testid="klant-kpi-overdue" />
          <KpiPill label="Betaald" value={wsSummary.counts?.invoices_paid ?? 0} tone="emerald" testid="klant-kpi-invoices-paid" />
          <KpiPill label="Open offertes" value={wsSummary.counts?.quotes_open ?? 0} tone="brand" testid="klant-kpi-quotes-open" />
          <KpiPill label="Actieve boekingen" value={wsSummary.counts?.bookings_active ?? 0} tone="brand" testid="klant-kpi-bookings-active" />
          <KpiPill label="Actieve zendingen" value={wsSummary.counts?.shipments_active ?? 0} tone="brand" testid="klant-kpi-shipments-active" />
          <KpiPill label="Laatste contact" value={wsSummary.latest_contact?.occurred_at ? fmtTs(wsSummary.latest_contact.occurred_at) : "—"} tone="slate" testid="klant-kpi-last-contact" small />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-white border border-gray-200 p-0 h-auto flex flex-wrap" data-testid="klant-tabs">
          <TabsTrigger value="overzicht" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-overzicht">Overzicht</TabsTrigger>
          <TabsTrigger value="communicatie" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-communicatie">Communicatie</TabsTrigger>
          <TabsTrigger value="contactgegevens" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-contact">
            Contactgegevens{proposalCount > 0 && <span className="ml-2 inline-block bg-yellow-400 text-[#0A192F] text-[10px] font-bold px-1.5 py-0.5">{proposalCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="offertes" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-offertes">Offertes ({quotes.length})</TabsTrigger>
          <TabsTrigger value="facturen" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-facturen">Facturen{wsSummary?.counts?.invoices_total > 0 && ` (${wsSummary.counts.invoices_total})`}</TabsTrigger>
          <TabsTrigger value="debiteuren" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-debiteuren">Debiteuren</TabsTrigger>
          <TabsTrigger value="boekingen" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-boekingen">Boekingen{wsSummary?.counts?.bookings_total > 0 && ` (${wsSummary.counts.bookings_total})`}</TabsTrigger>
          <TabsTrigger value="zendingen" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-zendingen">Zendingen{wsSummary?.counts?.shipments_total > 0 && ` (${wsSummary.counts.shipments_total})`}</TabsTrigger>
          <TabsTrigger value="pickup" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-pickup">Pickup & Delivery</TabsTrigger>
          <TabsTrigger value="documenten" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-documenten">Documenten</TabsTrigger>
          <TabsTrigger value="timeline" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-timeline">Tijdlijn</TabsTrigger>
          <TabsTrigger value="notities" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-notities">Notities</TabsTrigger>
          <TabsTrigger value="tarieven" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-tarieven">Tarieven</TabsTrigger>
          <TabsTrigger value="openstaand" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-openstaand">Openstaand</TabsTrigger>
          <TabsTrigger value="audit" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="klant-tab-audit">Activiteiten</TabsTrigger>
        </TabsList>

        <TabsContent value="overzicht" className="mt-2 space-y-3" data-testid="klant-overzicht-tab">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SectionCard title="Klantgegevens" testid="klant-card-klantgegevens">
              <Row label="Voornaam" value={c.first_name} testid="klant-card-first-name" />
              <Row label="Achternaam" value={c.last_name} testid="klant-card-last-name" />
              <Row label="Bedrijfsnaam" value={c.company_name} testid="klant-card-company-name" />
              <Row label="Klanttype" value={<CustomerTypeBadge type={c.customer_type} size="sm" testid="klant-card-customer-type-badge" />} testid="klant-card-customer-type" />
              <Row label="Status" value={
                <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${STATUS_COLORS[c.status] || "bg-gray-100"}`}>
                  {STATUS_LABELS[c.status] || c.status}
                </span>
              } testid="klant-card-status" />
            </SectionCard>

            <SectionCard title="Contactgegevens" testid="klant-card-contactgegevens">
              <Row label="E-mail" value={c.email} testid="klant-card-email" />
              <Row label="Telefoon" value={c.phone} testid="klant-card-phone" />
              <Row label="WhatsApp" value={c.whatsapp} testid="klant-card-whatsapp" />
              <Row label="Laatste contact" value={fmtTs(c.last_contact_at)} testid="klant-card-last-contact" />
            </SectionCard>

            <SectionCard title="Adresgegevens" testid="klant-card-adresgegevens">
              <Row label="Straat" value={c.address} testid="klant-card-street" />
              <Row label="Huisnr." value={[c.house_number, c.house_number_addition].filter(Boolean).join(" ") || null} testid="klant-card-house-number" />
              <Row label="Postcode" value={c.postal_code} testid="klant-card-postal-code" />
              <Row label="Plaats" value={c.city} testid="klant-card-city" />
              <Row label="Land" value={c.country} testid="klant-card-country" />
            </SectionCard>

            <SectionCard title="Administratieve gegevens" testid="klant-card-administratief">
              {c.btw_number && <Row label="BTW-nummer" value={c.btw_number} testid="klant-card-btw" />}
              {c.kvk_number && <Row label="KVK-nummer" value={c.kvk_number} testid="klant-card-kvk" />}
              {c.notes ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-bold mb-0.5">Notities</div>
                  <p className="text-sm whitespace-pre-wrap text-gray-800" data-testid="klant-card-notes">{c.notes}</p>
                </div>
              ) : null}
              {!c.btw_number && !c.kvk_number && !c.notes && (
                <div className="text-xs text-gray-400 italic">Geen administratieve gegevens vastgelegd.</div>
              )}
            </SectionCard>

            <SectionCard title="Import / herkomst" testid="klant-card-herkomst">
              <Row label="Bron" value={c.source} testid="klant-card-source" />
              <Row label="Aangemaakt op" value={fmtTs(c.created_at)} testid="klant-card-created-at" />
              <Row label="Laatst bijgewerkt" value={fmtTs(c.updated_at)} testid="klant-card-updated-at" />
              <Row label="Actief?" value={
                c.disabled
                  ? <span className="inline-block px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] uppercase tracking-wide font-bold">Disabled</span>
                  : <span className="inline-block px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] uppercase tracking-wide font-bold">Actief</span>
              } testid="klant-card-disabled" />
              {c.import_batch_id && <Row label="Import batch" value={c.import_batch_id} mono testid="klant-card-import-batch-id" />}
              {c.last_merge_batch_id && <Row label="Laatste merge" value={c.last_merge_batch_id} mono testid="klant-card-last-merge-batch-id" />}
              {c.repaired_by_batch_id && <Row label="Hersteld door" value={c.repaired_by_batch_id} mono testid="klant-card-repaired-by-batch-id" />}
            </SectionCard>

            <SectionCard title="Activiteit" testid="klant-card-activiteit">
              {/* P1 — Activiteit-rijen zijn aanklikbaar als navigatiehub.
                  Offertes/Pickups switchen lokaal naar de bijbehorende tab;
                  Facturen/Labels openen de globale sectie via onNavigateSection.
                  Bij ontbrekende permissie of count=0 wordt de klik
                  uitgeschakeld en disabled gestyled. */}
              {[
                { label: "Offertes", count: activity.quote_count ?? 0,
                  testid: "klant-card-quote-count", target: "offertes",
                  internal: true, perm: "quotes:view" },
                { label: "Pickups", count: activity.pickup_count ?? 0,
                  testid: "klant-card-pickup-count", target: "pickup",
                  internal: true, perm: "pickup:view" },
                { label: "Facturen", count: activity.invoice_count ?? 0,
                  testid: "klant-card-invoice-count", target: "facturen",
                  internal: false, perm: "quotes:view" },
                { label: "Labels", count: activity.label_count ?? 0,
                  testid: "klant-card-label-count", target: "tracking_labels",
                  internal: false, perm: "quotes:view" },
              ].map((r) => {
                const allowed = hasPermission(r.perm);
                const navigable = allowed && (r.internal || !!onNavigateSection);
                return (
                  <button
                    key={r.target}
                    type="button"
                    onClick={() => {
                      if (!navigable) return;
                      if (r.internal) {
                        setTab(r.target);
                      } else if (onNavigateSection) {
                        // P1 — Klantfilter doorgeven aan de globale sectie.
                        onNavigateSection(r.target, {
                          type: "customer",
                          customer_id: c.id,
                          customer_email: c.email || "",
                          customer_name: [c.first_name, c.last_name].filter(Boolean).join(" ")
                            || c.company_name
                            || c.email
                            || "Onbekend",
                        });
                      }
                    }}
                    disabled={!navigable}
                    title={!allowed ? "Geen toegang" : undefined}
                    className={
                      "w-full flex items-center justify-between px-2 py-1 -mx-2 transition-colors "
                      + (navigable
                        ? "hover:bg-blue-50 cursor-pointer"
                        : "cursor-not-allowed opacity-60")
                    }
                    data-testid={`klant-activity-nav-${r.target}`}
                  >
                    <span className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">{r.label}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-gray-800 font-medium" data-testid={r.testid}>{r.count}</span>
                      {navigable && (
                        <ChevronRight className="w-3.5 h-3.5 text-[#0055FF]" aria-hidden="true" />
                      )}
                    </span>
                  </button>
                );
              })}
              <OverzichtRow label="Open offertes" value={String(
                quotes.filter((q) => !["goedgekeurd", "afgewezen", "verlopen", "omgezet"].includes(q.status)).length
              )} testid="klant-card-open-quote-count" />
            </SectionCard>
          </div>

          {canViewTechnical && (
            <div className="bg-gray-50 border border-gray-200 p-4" data-testid="klant-card-technical">
              <button
                type="button"
                onClick={() => setShowTechnical((v) => !v)}
                className="text-[11px] uppercase tracking-wider font-bold text-gray-600 hover:text-[#0055FF] inline-flex items-center gap-2"
                data-testid="klant-card-technical-toggle"
              >
                {showTechnical ? "▼" : "▶"} Technische details (alleen Owner/Admin)
              </button>
              {showTechnical && (
                <div className="mt-3 space-y-1.5" data-testid="klant-card-technical-content">
                  <Row label="Klant-ID" value={c.id} mono testid="klant-card-id" />
                  <Row label="phone_norm" value={c.phone_norm} mono testid="klant-card-phone-norm" />
                  <Row label="whatsapp_norm" value={c.whatsapp_norm} mono testid="klant-card-whatsapp-norm" />
                  <Row label="address_key" value={c.address_key} mono testid="klant-card-address-key" />
                  <Row label="postal_code_normalized" value={c.postal_code_normalized} mono testid="klant-card-postal-code-normalized" />
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contactgegevens" className="bg-white border border-gray-200 p-6 mt-2">
          {proposalCount > 0 && (
            <div className="mb-6" data-testid="klant-proposals-list">
              <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" /> Wijzigingsvoorstellen
              </h3>
              <ul className="space-y-2">
                {proposals.map((p) => (
                  <li key={p.id} className="border border-yellow-300 bg-yellow-50 p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`klant-proposal-${p.id}`}>
                    <div>
                      <div className="text-xs uppercase tracking-wide font-bold text-yellow-800">{FIELD_LABELS[p.field] || p.field}</div>
                      <div className="text-sm mt-1">
                        Bestaand: <strong className="line-through">{p.old_value || "—"}</strong>
                        <span className="mx-2">→</span>
                        Voorstel: <strong className="text-green-700">{p.new_value || "—"}</strong>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">Uit offerte {(p.quote_id || "").slice(0, 8)}… · {fmtTs(p.created_at)}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => onResolveProposal(p.id, "accept")} className="inline-flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs uppercase font-chivo font-bold tracking-wide" data-testid={`klant-proposal-accept-${p.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Accepteren
                      </button>
                      <button onClick={() => onResolveProposal(p.id, "reject")} className="inline-flex items-center px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs uppercase font-chivo font-bold tracking-wide" data-testid={`klant-proposal-reject-${p.id}`}>
                        <X className="w-3.5 h-3.5 mr-1" /> Afwijzen
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-4">Klantgegevens</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {CONTACT_FIELDS.map((k) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wide font-bold">{FIELD_LABELS[k]}</Label>
                <Input
                  data-testid={`klant-field-${k}`}
                  type={k === "email" ? "email" : "text"}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  disabled={!canEditContact}
                  className="rounded-none mt-1"
                />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Type klant</Label>
              <Select value={form.customer_type || "particulier"} onValueChange={(v) => setForm({ ...form, customer_type: v })} disabled={!canEditFull}>
                <SelectTrigger className="rounded-none mt-1" data-testid="klant-field-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIMARY_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`klant-field-type-option-${t}`}>{TYPE_LABELS[t]}</SelectItem>)}
                  <div className="border-t border-gray-100 my-1" />
                  {LEGACY_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`klant-field-type-option-${t}`}>{TYPE_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {/* Batch 3 · Fase 2 — Agent classification toggle. Alleen wijzigbaar
                door Owner/Admin (canEditFull). Verandert het customer/company
                record NIET; het is enkel een classificatie zodat operationele
                Agents apart filterbaar zijn in de customer-lijst. */}
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">Agent</Label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="checkbox"
                  id="klant-field-is-agent"
                  data-testid="klant-field-is-agent"
                  className="h-4 w-4 accent-black cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  checked={!!form.is_agent}
                  disabled={!canEditFull}
                  onChange={(e) => setForm({ ...form, is_agent: e.target.checked })}
                />
                <label htmlFor="klant-field-is-agent" className="text-sm text-gray-700 select-none">
                  Deze klant fungeert als Agent
                </label>
              </div>
            </div>
          </div>

          {/* P1 — gestructureerd NAW-blok met PDOK-postcode/huisnummer lookup.
              NAW.street wordt opgeslagen in `address` (legacy single-line),
              huisnummer + toevoeging behouden eigen velden. */}
          <div className="mt-4">
            <NAWAddressBlock
              value={{
                street: form.address || "",
                house_number: form.house_number || "",
                house_number_addition: form.house_number_addition || "",
                postal_code: form.postal_code || "",
                city: form.city || "",
                country: form.country || "",
              }}
              onChange={(patch) => {
                setForm((prev) => {
                  const next = { ...prev };
                  if (Object.prototype.hasOwnProperty.call(patch, "street")) next.address = patch.street;
                  if (Object.prototype.hasOwnProperty.call(patch, "house_number")) next.house_number = patch.house_number;
                  if (Object.prototype.hasOwnProperty.call(patch, "house_number_addition")) next.house_number_addition = patch.house_number_addition;
                  if (Object.prototype.hasOwnProperty.call(patch, "postal_code")) next.postal_code = patch.postal_code;
                  if (Object.prototype.hasOwnProperty.call(patch, "city")) next.city = patch.city;
                  if (Object.prototype.hasOwnProperty.call(patch, "country")) next.country = patch.country;
                  return next;
                });
              }}
              disabled={!canEditContact}
              testidPrefix="klant-naw"
              title="Adres (postcode + huisnummer voor automatisch aanvullen)"
              hint="Vul postcode + huisnummer in voor NL — straat en plaats worden automatisch opgehaald via PDOK."
            />
          </div>

          {/* P1 — Administratieve B2B-velden (optioneel; alleen full-edit
              rollen mogen wijzigen). Worden in het Overzicht enkel getoond
              wanneer ingevuld. */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">BTW-nummer</Label>
              <Input
                data-testid="klant-field-btw_number"
                value={form.btw_number || ""}
                onChange={(e) => setForm({ ...form, btw_number: e.target.value })}
                disabled={!canEditFull}
                placeholder="NL123456789B01"
                className="rounded-none mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold">KVK-nummer</Label>
              <Input
                data-testid="klant-field-kvk_number"
                value={form.kvk_number || ""}
                onChange={(e) => setForm({ ...form, kvk_number: e.target.value })}
                disabled={!canEditFull}
                placeholder="12345678"
                className="rounded-none mt-1"
              />
            </div>
          </div>
          {/* Batch 3 · Fase 3 — Extra contactpersonen bewerkbaar op klantniveau.
              Contact-edit permission volstaat (net als reguliere contactvelden). */}
          <AdditionalContactsBlock
            value={form.additional_contacts || []}
            onChange={(next) => setForm({ ...form, additional_contacts: next })}
            disabled={!canEditContact}
          />
          {canEditContact && (
            <div className="mt-4">
              <Button onClick={onSave} disabled={saving} data-testid="klant-save-button" className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Opslaan..." : "Wijzigingen opslaan"}
              </Button>
            </div>
          )}

          <CustomerRecipientsPanel customerId={c.id} />
        </TabsContent>

        <TabsContent value="offertes" className="bg-white border border-gray-200 p-6 mt-2">
          <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#0055FF]" /> Gekoppelde offertes ({quotes.length})
          </h3>
          {quotes.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen offertes voor deze klant.</p>
          ) : (
            <table className="w-full text-sm" data-testid="klant-quotes-table">
              <thead className="bg-[#F8F9FA] border-b border-gray-200 text-left">
                <tr>
                  <th className="p-2 text-xs uppercase tracking-wide font-bold">Datum</th>
                  <th className="p-2 text-xs uppercase tracking-wide font-bold">Service</th>
                  <th className="p-2 text-xs uppercase tracking-wide font-bold">Bestemming</th>
                  <th className="p-2 text-xs uppercase tracking-wide font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} className="border-b border-gray-100" data-testid={`klant-quote-${q.id}`}>
                    <td className="p-2 text-xs">{q.created_at?.slice(0, 10)}</td>
                    <td className="p-2 text-xs">{q.service || "—"}</td>
                    <td className="p-2 text-xs">{q.destination || q.destination_country || "—"}</td>
                    <td className="p-2 text-xs">{QUOTE_STATUS_LABELS[q.status] || q.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TabsContent>

        <TabsContent value="tarieven" className="mt-2" data-testid="klant-tarieven-tab">
          <KlantTarievenTab customerId={customerId} />
        </TabsContent>

        <TabsContent value="notities" className="bg-white border border-gray-200 p-6 mt-2">
          <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-3">Algemene notities</h3>
          <Textarea
            data-testid="klant-field-notes"
            rows={6}
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            disabled={!canEditFull}
            className="rounded-none"
            placeholder="Algemene notities over deze klant. Voor offerte-specifieke notities, gebruik de offerte-detailpagina."
          />
          {!canEditFull && <div className="text-[10px] text-gray-500 mt-1">Alleen manager/admin/owner kan notities bewerken.</div>}
          {canEditFull && (
            <div className="mt-4">
              <Button onClick={onSave} disabled={saving} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs">
                <Save className="w-4 h-4 mr-1" /> Opslaan
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="bg-white border border-gray-200 p-6 mt-2">
          <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-[#0055FF]" /> Timeline ({timeline.length})
          </h3>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen events.</p>
          ) : (
            <ul className="relative pl-6 border-l-2 border-gray-200 space-y-4" data-testid="klant-timeline-list">
              {timeline.map((e, i) => (
                <li key={i} className="relative" data-testid={`klant-timeline-event-${i}`}>
                  <div className="absolute -left-[1.85rem] top-1 w-3 h-3 rounded-full bg-[#0055FF] border-2 border-white"></div>
                  <div className="text-xs text-gray-500">{fmtTs(e.ts)}</div>
                  <div className="font-chivo font-bold text-sm text-[#0A192F] mt-0.5">{e.description}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    <span className="inline-block bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-bold mr-1">{e.type}</span>
                    door {e.actor}
                    {e.quote_id && <span className="ml-2 font-mono">offerte:{e.quote_id.slice(0, 8)}…</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        <TabsContent value="pickup" className="bg-white border border-dashed border-gray-300 p-6 mt-2">
          <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-2 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" /> Pickup & Delivery (fase 2B)
          </h3>
          <p className="text-sm text-gray-500">Toekomstige afspraken: {pickups.length}. Volledige planning komt in fase 2B.</p>
        </TabsContent>

        <TabsContent value="openstaand" className="mt-2">
          <KlantOpenstaandTab customerId={c.id} />
        </TabsContent>

        <TabsContent value="audit" className="bg-white border border-gray-200 p-6 mt-2">
          <h3 className="font-chivo font-black text-lg text-[#0A192F] mb-3 flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-[#0055FF]" /> Activiteiten ({auditLogs.length})
          </h3>
          {auditLogs.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen log-entries.</p>
          ) : (
            <ul className="space-y-2 text-xs" data-testid="klant-audit-list">
              {auditLogs.map((a) => (
                <li key={a.id} className="border border-gray-200 p-2" data-testid={`klant-audit-${a.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">{fmtTs(a.created_at)}</span>
                    <span className="font-chivo font-bold">{a.action}</span>
                    <span className="text-gray-500">door {a.actor_email || "—"}</span>
                  </div>
                  {(a.old_value || a.new_value) && (
                    <div className="mt-1 text-gray-600">
                      {a.old_value && <span>oud: <code className="bg-gray-100 px-1">{JSON.stringify(a.old_value)}</code></span>}
                      {a.new_value && <span className="ml-2">nieuw: <code className="bg-green-50 px-1">{JSON.stringify(a.new_value)}</code></span>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>

        {/* 360° Customer Workspace tabs · Feb 2026 */}
        <TabsContent value="communicatie" className="bg-white border border-gray-200 p-6 mt-2" data-testid="klant-communicatie-tab">
          <CustomerCommunicationsPane
            customerId={c.id}
            items={wsCommunications}
            onNavigate={onNavigateSection}
          />
        </TabsContent>

        <TabsContent value="facturen" className="mt-2" data-testid="klant-facturen-tab">
          <CustomerInvoicesPane
            items={wsInvoices}
            summary={wsSummary}
            customerId={c.id}
            onNavigate={onNavigateSection}
          />
        </TabsContent>

        <TabsContent value="debiteuren" className="mt-2" data-testid="klant-debiteuren-tab">
          <CustomerDebtorPane
            customerId={c.id}
            summary={wsSummary}
            onNavigate={onNavigateSection}
          />
        </TabsContent>

        <TabsContent value="boekingen" className="bg-white border border-gray-200 p-6 mt-2" data-testid="klant-boekingen-tab">
          <CustomerBookingsPane
            items={wsBookings}
            onNavigate={onNavigateSection}
          />
        </TabsContent>

        <TabsContent value="zendingen" className="bg-white border border-gray-200 p-6 mt-2" data-testid="klant-zendingen-tab">
          <CustomerShipmentsPane
            items={wsShipments}
            onNavigate={onNavigateSection}
          />
        </TabsContent>

        <TabsContent value="documenten" className="bg-white border border-gray-200 p-6 mt-2" data-testid="klant-documenten-tab">
          <CustomerDocumentsPane customerId={c.id} onNavigate={onNavigateSection} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 360° Customer Workspace panes · Feb 2026
// ─────────────────────────────────────────────────────────────────────

function KpiPill({ label, value, tone = "slate", testid, small }) {
  const toneMap = {
    emerald: "text-emerald-800 bg-emerald-50 border-emerald-200",
    amber:   "text-amber-800 bg-amber-50 border-amber-200",
    red:     "text-red-800 bg-red-50 border-red-200",
    brand:   "text-brand-900 bg-brand-50 border-brand-100",
    slate:   "text-slate-700 bg-slate-50 border-slate-200",
  };
  const cls = toneMap[tone] || toneMap.slate;
  return (
    <div className={`border ${cls} p-2 rounded-sm`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.12em] font-semibold text-slate-500">{label}</div>
      <div className={`${small ? "text-[11px]" : "text-sm"} font-bold mt-0.5 truncate`}>{value ?? "—"}</div>
    </div>
  );
}

function fmtEur(v) {
  if (v == null || v === 0) return "€ 0,00";
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency", currency: "EUR",
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(v));
  } catch { return `€ ${Number(v).toFixed(2)}`; }
}

function CustomerCommunicationsPane({ customerId, items, onNavigate }) {
  if (items === null) return <div className="text-sm text-gray-500">Laden…</div>;
  if (!items || items.length === 0) {
    return (
      <div className="p-8 text-center border border-dashed border-gray-300">
        <MessageSquareIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <div className="text-sm text-gray-500">Nog geen communicatie voor deze klant.</div>
      </div>
    );
  }
  const iconMap = {
    reminder: "🔔",
    email: "✉️",
    stripe: "💳",
    payment: "💰",
    note: "📝",
  };
  return (
    <div className="space-y-2" data-testid="klant-communicatie-list">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-chivo font-black text-base text-[#0A192F]">Communicatiehistorie ({items.length})</h3>
        <Button
          size="sm" variant="outline"
          onClick={() => onNavigate?.("communicatie")}
          className="rounded-none text-[10px] uppercase font-chivo font-bold"
          data-testid="klant-open-communicatiecentrum"
        >
          Open in Communicatiecentrum →
        </Button>
      </div>
      <ul className="divide-y divide-gray-100 border border-gray-200 bg-white">
        {items.map((it) => (
          <li key={`${it.type}-${it.id}`} className="p-3 flex items-start gap-3 hover:bg-brand-50/30" data-testid={`klant-comm-${it.id}`}>
            <div className="text-lg flex-shrink-0" aria-hidden>{iconMap[it.type] || "📨"}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-brand-900 truncate">{it.subject || it.email_type || it.stripe_event_type || "—"}</div>
              <div className="text-[11px] text-gray-500 truncate">
                {it.occurred_at ? fmtTs(it.occurred_at) : ""}
                {it.customer_email && ` · ${it.customer_email}`}
                {it.amount != null && ` · ${fmtEur(it.amount)}`}
              </div>
            </div>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 flex-shrink-0 uppercase tracking-wide">
              {it.channel || it.type}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CustomerInvoicesPane({ items, summary, customerId, onNavigate }) {
  if (items === null) return <div className="text-sm text-gray-500 p-4">Laden…</div>;
  const cnt = summary?.counts || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <KpiPill label="Totaal facturen" value={cnt.invoices_total ?? 0} tone="brand" testid="klant-fact-kpi-total" />
        <KpiPill label="Openstaand" value={cnt.invoices_open ?? 0} tone={cnt.invoices_open > 0 ? "amber" : "slate"} testid="klant-fact-kpi-open" />
        <KpiPill label="Betaald" value={cnt.invoices_paid ?? 0} tone="emerald" testid="klant-fact-kpi-paid" />
        <KpiPill label="Openstaand bedrag" value={fmtEur(summary?.financials?.outstanding_total)} tone="amber" testid="klant-fact-kpi-outstanding" />
      </div>
      {(!items || items.length === 0) ? (
        <div className="p-8 text-center border border-dashed border-gray-300">
          <div className="text-sm text-gray-500">Deze klant heeft nog geen facturen.</div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="klant-facturen-list">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wide font-chivo font-bold text-gray-500">
              <tr>
                <th className="p-2 text-left">Nr.</th>
                <th className="p-2 text-left">Datum</th>
                <th className="p-2 text-left">Vervaldatum</th>
                <th className="p-2 text-right">Totaal</th>
                <th className="p-2 text-right">Openstaand</th>
                <th className="p-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.slice(0, 30).map((inv) => (
                <tr key={inv.id} className="hover:bg-brand-50/30" data-testid={`klant-fact-row-${inv.id}`}>
                  <td className="p-2 font-mono text-brand-900">{inv.invoice_number || inv.id?.slice(0, 8)}</td>
                  <td className="p-2 text-gray-600">{inv.invoice_date || "—"}</td>
                  <td className="p-2 text-gray-600">{inv.due_date || "—"}</td>
                  <td className="p-2 text-right">{fmtEur(inv.total_incl_vat)}</td>
                  <td className="p-2 text-right font-semibold">{fmtEur(inv.amount_due)}</td>
                  <td className="p-2"><span className="text-[10px] uppercase font-bold text-gray-700">{inv.financial_status || "—"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CustomerDebtorPane({ customerId, summary, onNavigate }) {
  const fin = summary?.financials || {};
  const cnt = summary?.counts || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <KpiPill label="Openstaand saldo" value={fmtEur(fin.outstanding_total)} tone="brand" testid="klant-deb-kpi-outstanding" />
        <KpiPill label="Vervallen saldo" value={fmtEur(fin.overdue_total)} tone={fin.overdue_total > 0 ? "red" : "slate"} testid="klant-deb-kpi-overdue" />
        <KpiPill label="Betalingsregelingen open" value={fin.payment_plans_open ?? 0} tone="slate" testid="klant-deb-kpi-plans" />
        <KpiPill label="Open reminders" value={cnt.reminders_open ?? 0} tone="amber" testid="klant-deb-kpi-reminders" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 p-4" data-testid="klant-deb-last-reminder">
          <div className="text-[10px] uppercase tracking-wide font-chivo font-bold text-gray-500 mb-1">Laatste herinnering</div>
          {fin.last_reminder_sent ? (
            <>
              <div className="text-sm font-semibold text-brand-900">{fin.last_reminder_sent.stage || "—"}</div>
              <div className="text-xs text-gray-500">{fmtTs(fin.last_reminder_sent.sent_at)}</div>
            </>
          ) : <div className="text-sm text-gray-400 italic">Nog geen herinneringen verzonden</div>}
        </div>
        <div className="bg-white border border-gray-200 p-4" data-testid="klant-deb-next-reminder">
          <div className="text-[10px] uppercase tracking-wide font-chivo font-bold text-gray-500 mb-1">Volgende geplande herinnering</div>
          {fin.next_reminder_planned ? (
            <>
              <div className="text-sm font-semibold text-brand-900">{fin.next_reminder_planned.stage || "—"}</div>
              <div className="text-xs text-gray-500">{fin.next_reminder_planned.scheduled_date}</div>
            </>
          ) : <div className="text-sm text-gray-400 italic">Geen geplande herinneringen</div>}
        </div>
      </div>
      <Button
        variant="outline"
        onClick={() => onNavigate?.("debiteuren")}
        className="rounded-none text-[10px] uppercase font-chivo font-bold"
        data-testid="klant-open-debiteuren"
      >
        Open in Debiteurenbeheer →
      </Button>
    </div>
  );
}

function CustomerBookingsPane({ items, onNavigate }) {
  if (items === null) return <div className="text-sm text-gray-500">Laden…</div>;
  if (!items || items.length === 0) {
    return <div className="p-6 text-center text-sm text-gray-500 italic">Geen boekingen voor deze klant.</div>;
  }
  return (
    <ul className="divide-y divide-gray-100" data-testid="klant-boekingen-list">
      {items.slice(0, 20).map((b) => (
        <li key={b.id} className="p-3 flex justify-between hover:bg-brand-50/30">
          <div>
            <div className="font-semibold text-brand-900 text-sm">{b.booking_number || b.reference || b.id?.slice(0, 8)}</div>
            <div className="text-[11px] text-gray-500">{b.status || "—"} · {b.destination || ""} · {b.created_at ? fmtTs(b.created_at) : ""}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function CustomerShipmentsPane({ items, onNavigate }) {
  if (items === null) return <div className="text-sm text-gray-500">Laden…</div>;
  if (!items || items.length === 0) {
    return <div className="p-6 text-center text-sm text-gray-500 italic">Geen zendingen voor deze klant.</div>;
  }
  return (
    <ul className="divide-y divide-gray-100" data-testid="klant-zendingen-list">
      {items.slice(0, 20).map((s) => (
        <li key={s.id} className="p-3 flex justify-between hover:bg-brand-50/30">
          <div>
            <div className="font-semibold text-brand-900 text-sm font-mono">{s.tracking_number || s.w_number || s.id?.slice(0, 8)}</div>
            <div className="text-[11px] text-gray-500">{s.process_status || s.status || "—"} · {s.created_at ? fmtTs(s.created_at) : ""}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function CustomerDocumentsPane({ customerId, onNavigate }) {
  return (
    <div className="p-8 text-center border border-dashed border-gray-300">
      <div className="text-sm font-semibold text-brand-900 mb-1">Documenten & PDFs</div>
      <div className="text-xs text-gray-500 max-w-md mx-auto mb-3">
        Alle documenten (paklijsten, boekingsformulieren, factuur-PDFs, briefpapier) worden aggregatief getoond zodra de klantdocumenten-index-endpoint live is. Voor nu: gebruik de Documenten-module.
      </div>
      <Button
        variant="outline"
        onClick={() => onNavigate?.("documenten")}
        className="rounded-none text-[10px] uppercase font-chivo font-bold"
        data-testid="klant-open-documenten"
      >
        Open documentenmodule →
      </Button>
    </div>
  );
}
